/**************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2022 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/

import { useAsyncList } from '@react-stately/data';

/**
 * Hook that returns the useAsyncList interface that is used by many React Spectrum v3 views (e.g. TableView). It
 * fetches data from the AdminDataServlet, like for example associated content data.
 */
const useReferenceData = props => {
    const {
        type,
        identifier,
        operation,
        origin = window.location.origin,
        servlet = '/bin/cifadmindata.json',
        limit = 20
    } = props;

    const buildQuery = cursor => {
        let req = new URL(`${origin}${servlet}`);
        req.searchParams.append('operation', operation);
        req.searchParams.append('type', type);
        req.searchParams.append('identifier', identifier);
        if (cursor > 0) {
            req.searchParams.append('offset', cursor);
        }

        return req;
    };

    const mapDate = item => {
        let lastModified = item.lastModified;
        try {
            lastModified = new Date(lastModified);
        } catch (err) {
            lastModified = NaN;
        }

        // Invalid dates that do not throw
        if (isNaN(lastModified)) {
            lastModified = NaN;
        }

        return {
            ...item,
            lastModified
        };
    };

    const load = async ({ signal, cursor }) => {
        const query = buildQuery(cursor);
        try {
            const result = await fetch(query, { signal });
            const resultJson = await result.json();

            const listResult = {
                items: resultJson.map(mapDate)
            };

            // Only add cursor if we expect more items
            if (resultJson.length >= limit) {
                listResult.cursor = cursor + limit;
            }

            return listResult;
        } catch (err) {
            console.error(err);
            return { items: [] };
        }
    };

    return useAsyncList({ load });
};

export default useReferenceData;
